import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, MessageSquare, PhoneMissed, RefreshCw, Star } from "lucide-react";
import { Phone } from "./Phone";
import { Reveal, SectionHead } from "./shared";
import { DEMO_TABS } from "../../data/content";

const TAB_ICONS = {
    "new-lead": MessageSquare,
    "missed-call": PhoneMissed,
    appointment: CalendarCheck,
    "follow-up": RefreshCw,
    "review-request": Star,
};

const METRICS = [
    { value: "8s", label: "Avg. Response Time" },
    { value: "24/7", label: "Coverage" },
    { value: "100%", label: "Follow-Up Rate" },
];

export default function InteractiveDemo() {
    const [active, setActive] = useState(DEMO_TABS[0].id);
    const tab = DEMO_TABS.find((t) => t.id === active);

    return (
        <section id="demo" className="py-24 sm:py-32 relative overflow-hidden" data-testid="demo-section">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-cyan-500/[0.07] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative">
                <SectionHead
                    eyebrow="02 — Live Demo"
                    title="Don't Take Our Word For It. Watch It Work."
                    sub="Real conversations, handled end-to-end. Pick a scenario and watch your AI employee do the work."
                    testid="demo-heading"
                />

                <Reveal delay={0.1}>
                    <div className="mt-12 flex flex-wrap gap-2.5" role="tablist" data-testid="demo-tabs">
                        {DEMO_TABS.map((t) => {
                            const Icon = TAB_ICONS[t.id];
                            const isActive = t.id === active;
                            return (
                                <button
                                    key={t.id}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActive(t.id)}
                                    data-testid={`demo-tab-${t.id}`}
                                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-300 border ${
                                        isActive
                                            ? "bg-cyan-400/15 border-cyan-400/50 text-cyan-200"
                                            : "bg-white/[0.03] border-white/10 text-slate-400 hover:text-white hover:border-white/25"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </Reveal>

                <div className="mt-14 flex flex-col items-center">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        style={{ perspective: 1200 }}
                    >
                        <div style={{ transform: "rotateX(3deg) rotateY(-6deg)" }}>
                            <Phone script={tab.script} loop headerTitle={tab.label} testid="demo-phone" />
                        </div>
                    </motion.div>

                    <div className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 w-full max-w-2xl" data-testid="demo-metrics">
                        {METRICS.map((m, i) => (
                            <Reveal key={m.label} delay={i * 0.1}>
                                <div className="glass rounded-2xl px-4 py-5 text-center">
                                    <p className="ff-display text-2xl sm:text-3xl font-bold text-gradient">{m.value}</p>
                                    <p className="text-[10px] sm:text-xs text-slate-500 mt-1.5">{m.label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
